import { ScanResult, ScanSummary, createScanSummary } from './types';

/**
 * Memory Leak Detection Scan
 * Checks for common memory leak patterns in React applications
 */
export function runMemoryLeakScan(): ScanSummary {
  console.group('🧠 Memory Leak Detection Scan');
  
  const results: ScanResult[] = [];

  try {
    // ========== Check 1: Memory Usage Baseline ==========
    const memoryInfo = (performance as any).memory;
    
    if (memoryInfo) {
      const usedMB = (memoryInfo.usedJSHeapSize / 1024 / 1024).toFixed(2);
      const totalMB = (memoryInfo.totalJSHeapSize / 1024 / 1024).toFixed(2);
      const limitMB = (memoryInfo.jsHeapSizeLimit / 1024 / 1024).toFixed(2);
      const usagePercent = ((memoryInfo.usedJSHeapSize / memoryInfo.jsHeapSizeLimit) * 100).toFixed(1);

      results.push({
        name: 'JS Heap Usage',
        status: parseFloat(usagePercent) < 50 ? 'pass' : parseFloat(usagePercent) < 80 ? 'warn' : 'fail',
        message: `${usedMB} MB used of ${limitMB} MB limit (${usagePercent}%)`,
        details: `Heap allocated: ${totalMB} MB`
      });
    } else {
      results.push({
        name: 'JS Heap Usage',
        status: 'info',
        message: 'Memory API not available (non-Chrome browser)',
        details: 'Use Chrome DevTools for detailed memory profiling'
      });
    }

    // ========== Check 2: DOM Node Count ==========
    const allNodes = document.querySelectorAll('*');
    const nodeCount = allNodes.length;
    
    // Typical healthy range: 500-2000 nodes for a game app
    results.push({
      name: 'DOM Node Count',
      status: nodeCount < 2000 ? 'pass' : nodeCount < 5000 ? 'warn' : 'fail',
      message: `${nodeCount} DOM nodes`,
      details: nodeCount > 3000 
        ? 'High node count may indicate DOM leak or inefficient rendering'
        : 'Node count is within healthy range'
    });

    // ========== Check 3: Event Listener Estimation ==========
    // Can't directly count listeners, but can check for common leak patterns
    const interactiveElements = document.querySelectorAll('button, a, input, [onclick], [onchange], [onkeydown]');
    
    results.push({
      name: 'Interactive Elements',
      status: interactiveElements.length < 200 ? 'pass' : 'info',
      message: `${interactiveElements.length} interactive element(s)`,
      details: 'Each may have attached event listeners'
    });

    // ========== Check 4: Detached DOM Elements (Indirect Check) ==========
    // Check for elements that might be orphaned
    const hiddenElements = document.querySelectorAll('[style*="display: none"], .hidden, [hidden]');
    const offscreenElements = Array.from(document.querySelectorAll('*')).filter(el => {
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && 
             (rect.bottom < -1000 || rect.top > window.innerHeight + 1000);
    });

    results.push({
      name: 'Hidden/Offscreen Elements',
      status: 'info',
      message: `${hiddenElements.length} hidden, ${offscreenElements.length} offscreen`,
      details: 'Review if these should be unmounted instead of hidden'
    });

    // ========== Check 5: Timer/Interval Detection ==========
    // Check window for common timer patterns (indirect)
    const timerWarnings: string[] = [];
    
    // Look for potential setInterval leaks by checking common patterns
    if ((window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      results.push({
        name: 'React DevTools',
        status: 'info',
        message: 'React DevTools detected - use Profiler for component analysis',
        details: 'Check for components that render excessively'
      });
    }

    // ========== Check 6: Image/Resource Count ==========
    const images = document.querySelectorAll('img');
    const loadedImages = Array.from(images).filter(img => img.complete);
    const largeImages = Array.from(images).filter(img => 
      img.naturalWidth > 1000 || img.naturalHeight > 1000
    );

    results.push({
      name: 'Image Resources',
      status: largeImages.length === 0 ? 'pass' : 'info',
      message: `${images.length} images (${loadedImages.length} loaded, ${largeImages.length} large)`,
      details: largeImages.length > 0 
        ? 'Large images may increase memory usage'
        : 'All images are reasonably sized'
    });

    // ========== Check 7: LocalStorage Bloat ==========
    let localStorageSize = 0;
    for (const key of Object.keys(localStorage)) {
      localStorageSize += (localStorage.getItem(key) || '').length * 2;
    }
    const lsKB = (localStorageSize / 1024).toFixed(2);

    results.push({
      name: 'LocalStorage Size',
      status: localStorageSize < 1024 * 1024 ? 'pass' : 'warn',
      message: `${lsKB} KB in localStorage`,
      details: localStorageSize > 1024 * 1024 
        ? 'Consider cleaning old data to reduce memory footprint'
        : 'Storage usage is healthy'
    });

    // ========== Check 8: Canvas/WebGL Check ==========
    const canvases = document.querySelectorAll('canvas');
    
    results.push({
      name: 'Canvas Elements',
      status: canvases.length < 5 ? 'pass' : 'warn',
      message: `${canvases.length} canvas element(s)`,
      details: canvases.length > 0 
        ? 'Canvases can consume significant GPU memory'
        : 'No canvas elements detected'
    });

    // ========== Check 9: Iframe Count ==========
    const iframes = document.querySelectorAll('iframe');
    
    results.push({
      name: 'Iframe Elements',
      status: iframes.length < 3 ? 'pass' : 'warn',
      message: `${iframes.length} iframe(s)`,
      details: iframes.length > 0 
        ? 'Each iframe has its own memory context'
        : 'No iframes detected'
    });

    // ========== Check 10: Console Errors (Memory Leak Indicators) ==========
    // Check if there are patterns that might indicate issues
    const errorPatterns = [
      'ResizeObserver loop',
      'Memory',
      'Maximum call stack',
      'heap'
    ];
    
    results.push({
      name: 'Error Pattern Check',
      status: 'info',
      message: 'Check console for memory-related errors',
      details: `Watch for: ${errorPatterns.join(', ')}`
    });

    // ========== Check 11: Animation Frame Usage ==========
    const animatedElements = document.querySelectorAll('[class*="animate"], [class*="transition"]');
    
    results.push({
      name: 'Animated Elements',
      status: animatedElements.length < 20 ? 'pass' : 'info',
      message: `${animatedElements.length} animated element(s)`,
      details: 'CSS animations are generally GPU-accelerated and memory-efficient'
    });

    // ========== Check 12: Memory Growth Test ==========
    results.push({
      name: 'Memory Growth Test',
      status: 'info',
      message: 'Run this scan multiple times to detect memory growth',
      details: 'If heap usage increases over time without user action, there may be a leak'
    });

  } catch (error) {
    results.push({
      name: 'Memory Leak Scan Error',
      status: 'fail',
      message: `Scan failed: ${error}`,
      details: String(error)
    });
  }

  const summary = createScanSummary('Memory Leak Detection Scan', results);
  
  console.log(`✅ Passed: ${summary.passCount}`);
  console.log(`⚠️  Warnings: ${summary.warnCount}`);
  console.log(`❌ Failed: ${summary.failCount}`);
  console.table(results);
  console.groupEnd();
  
  return summary;
}
